import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { Survey, SurveyCustomer, SurveyDiscipline } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { getCustomerUrl, getKidUrl } from '@shared/lib/routes'
import { useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { SURVEY_EMPTY_CELL, useSurveyOptions } from './survey-options'
import { getDisciplineSurveysUrl } from './survey-links'
import { useSurveysStore } from './store'

interface TableSurveysReturn {
  surveys: ComputedRef<Survey[]>
  isLoading: ComputedRef<boolean>
  hasSurveys: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  emptyCell: string
  getTypeLabel: (survey: Survey) => string
  getCustomerName: (customer: SurveyCustomer) => string
  getDisciplineLabel: (discipline: SurveyDiscipline) => string
  getLessonId: (survey: Survey) => string
  getCreatedAt: (survey: Survey) => string
  getSourceLabel: (survey: Survey) => string
  getCustomerUrl: (customerId: number) => string
  getKidUrl: (kidId: number) => string
  getDisciplineSurveysUrl: (disciplineId: number) => string
  isExpanded: (survey: Survey) => boolean
  getAnswersRowId: (survey: Survey) => string
  toggleAnswers: (survey: Survey) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableSurveys = (): TableSurveysReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useSurveysStore()
  const dayjs = useDayjs()
  const {
    getTypeLabel: getTypeLabelByKey,
    getSourceLabel: getSourceLabelByKey,
    getDisciplineLabel: getDisciplineLabelByCode,
  } = useSurveyOptions()

  const expandedSurveyId = ref<number | null>(null)

  const surveys = computed<Survey[]>(() => store.getSurveys)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasSurveys = computed<boolean>(() => store.hasSurveys)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const getTypeLabel = (survey: Survey): string => getTypeLabelByKey(survey.type)

  const getCustomerName = (customer: SurveyCustomer): string =>
    `${customer.first_name} ${customer.last_name}`.trim()

  const getDisciplineLabel = (discipline: SurveyDiscipline): string => getDisciplineLabelByCode(discipline.code)

  const getLessonId = (survey: Survey): string =>
    survey.booking_id !== null ? String(survey.booking_id) : SURVEY_EMPTY_CELL

  const getCreatedAt = (survey: Survey): string => dayjs(survey.created_at).format(DateFormat.KanbanDate)

  const getSourceLabel = (survey: Survey): string => getSourceLabelByKey(survey.source)

  const isExpanded = (survey: Survey): boolean => expandedSurveyId.value === survey.id

  const getAnswersRowId = (survey: Survey): string => `survey-answers-${survey.id}`

  const toggleAnswers = (survey: Survey): void => {
    expandedSurveyId.value = isExpanded(survey) ? null : survey.id
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  watch(surveys, () => {
    expandedSurveyId.value = null
  })

  return {
    surveys,
    isLoading,
    hasSurveys,
    meta,
    perPageOptions,
    emptyCell: SURVEY_EMPTY_CELL,
    getTypeLabel,
    getCustomerName,
    getDisciplineLabel,
    getLessonId,
    getCreatedAt,
    getSourceLabel,
    getCustomerUrl,
    getKidUrl,
    getDisciplineSurveysUrl,
    isExpanded,
    getAnswersRowId,
    toggleAnswers,
    setChangePage,
    setChangePerPage,
  }
}
